import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Crown, Clock, CheckCircle2, XCircle, ArrowRight, Radio } from "lucide-react";

const field = "w-full bg-transparent border-b border-white/15 py-3 text-base focus:border-gold outline-none transition-colors placeholder:text-white/25";

const SpotsCounter = ({ offer }) => (
  <div data-testid="apply-spots-counter" className="mt-5 space-y-2">
    <div className="flex items-center justify-center gap-2 text-xs uppercase tracking-[0.25em] text-gold">
      <span className="relative flex w-2 h-2"><span className="absolute inset-0 rounded-full bg-gold animate-ping opacity-60" /><span className="relative w-2 h-2 rounded-full bg-gold" /></span>
      <span data-testid="apply-spots-left"><motion.b key={offer.left} initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} className="inline-block">{offer.left}</motion.b> of {offer.total} founding spots left</span>
    </div>
    <div className="h-1.5 rounded-full bg-white/10 overflow-hidden max-w-xs mx-auto">
      <motion.div data-testid="apply-spots-bar" className="h-full bg-gold" initial={false} animate={{ width: `${(offer.claimed / offer.total) * 100}%` }} transition={{ duration: 0.8 }} />
    </div>
    <p className="text-[10px] uppercase tracking-[0.2em] text-text-muted">{offer.claimed} claimed · updates live</p>
  </div>
);

const OfferHero = ({ offer }) => (
  <div className="text-center">
    <p className="text-[11px] uppercase tracking-[0.35em] text-gold flex items-center justify-center gap-2"><Crown size={14} /> Exclusive creator recruitment</p>
    <h1 className="font-display text-5xl sm:text-7xl font-light mt-5 leading-[0.95]">Be a part of the<br /><span className="brand-script text-6xl sm:text-8xl">Velluto</span> empire</h1>
    <p className="text-text-muted mt-6 max-w-lg mx-auto">Velluto Lounge live-stream models. Web launch 1st of November.</p>
    <div data-testid="apply-offer" className="mt-8 inline-block rounded-3xl border border-gold/40 bg-gold/5 px-6 sm:px-10 py-6">
      <p className="font-display text-3xl sm:text-4xl text-gold">Premium revenue</p>
      <p className="text-sm text-text-main mt-2">The first {offer?.total ?? 300} approved creators keep <b>100% of their earnings</b> for their first 3 months.</p>
      <p className="text-[11px] text-text-muted mt-1">Card processing fees and chargebacks still apply.</p>
      {offer && <SpotsCounter offer={offer} />}
    </div>
  </div>
);

const StatusCard = ({ a, isCreator }) => {
  const map = {
    pending: [Clock, "text-gold", "Application received", "Our team is reviewing it. You'll get a Velluto message as soon as there's a decision."],
    approved: [CheckCircle2, "text-emerald-400", "You're approved ✦", a.launch_offer ? "You're a founding creator — 100% of your earnings for your first 3 months." : "Welcome to Velluto Models."],
    rejected: [XCircle, "text-red-300", "Not approved this time", a.reason || "Thank you for applying."],
  };
  const [Icon, color, title, copy] = map[a.status];
  return (
    <div data-testid={`apply-status-${a.status}`} className="rounded-3xl border border-white/10 bg-surface p-8 text-center">
      <Icon size={30} className={`mx-auto ${color}`} />
      <h2 className="font-display text-3xl mt-4">{title}</h2>
      <p className="text-sm text-text-muted mt-2">{copy}</p>
      {(a.status === "approved" || isCreator) && (
        <Link to="/creator/register" data-testid="apply-open-studio" className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gold text-ink text-xs uppercase tracking-[0.2em]">
          <Radio size={14} /> Open creator studio
        </Link>
      )}
    </div>
  );
};

const ApplyForm = ({ user, onDone }) => {
  const [f, setF] = useState({ stage_name: user.display_name || "", email: user.email || "", country: "", confirm_18: false });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/creator/application", f); toast.success("Application sent ✦"); onDone(data.application); }
    catch (err) { toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Couldn't send your application"); }
    setBusy(false);
  };
  return (
    <form data-testid="apply-form" onSubmit={submit} className="rounded-3xl border border-white/10 bg-surface p-6 sm:p-10 space-y-6">
      <h2 className="font-display text-3xl">Apply as a creator</h2>
      <label className="block"><span className="text-[10px] uppercase tracking-[0.2em] text-text-muted">Stage name</span>
        <input data-testid="apply-stage-name" required minLength={2} maxLength={40} value={f.stage_name} onChange={set("stage_name")} className={field} placeholder="How fans will know you" /></label>
      <label className="block"><span className="text-[10px] uppercase tracking-[0.2em] text-text-muted">Email</span>
        <input data-testid="apply-email" type="email" required value={f.email} onChange={set("email")} className={field} /></label>
      <label className="block"><span className="text-[10px] uppercase tracking-[0.2em] text-text-muted">Country</span>
        <input data-testid="apply-country" required minLength={2} maxLength={60} value={f.country} onChange={set("country")} className={field} placeholder="e.g. Australia" /></label>
      <label className="flex items-start gap-3 cursor-pointer">
        <input data-testid="apply-confirm-18" type="checkbox" checked={f.confirm_18} onChange={set("confirm_18")} className="mt-1 w-4 h-4 accent-[#D4AF37]" />
        <span className="text-sm text-text-muted">I confirm I'm 18 or older and will complete Velluto's ID check before going live.</span>
      </label>
      <button data-testid="apply-submit" disabled={busy || !f.confirm_18}
        className="w-full py-4 rounded-full bg-gold text-ink text-xs uppercase tracking-[0.25em] hover:bg-gold/90 transition-colors disabled:opacity-40">
        {busy ? "Sending…" : "Submit application"}
      </button>
    </form>
  );
};

export default function Apply() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [offer, setOffer] = useState(null);
  const [state, setState] = useState(null);

  useEffect(() => {
    const pull = () => api.get("/creator-offer").then(({ data }) => setOffer(data)).catch(() => {});
    pull();
    const t = setInterval(pull, 20000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (!user) return;
    if (user.email_verified === false) { localStorage.setItem("velluto_after_confirm", "/apply"); nav("/confirm-email"); return; }
    api.get("/creator/application").then(({ data }) => setState(data)).catch(() => setState({}));
  }, [user, nav]);

  const go = (mode) => {
    sessionStorage.setItem("velluto_next", "/apply");
    localStorage.setItem("velluto_after_confirm", "/apply");
    nav(`/auth${mode === "register" ? "?mode=register" : ""}`);
  };

  return (
    <div data-testid="apply-page" className="pt-28 pb-24 max-w-3xl mx-auto px-5 sm:px-6">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="space-y-12">
        <OfferHero offer={offer} />
        {user === null ? null : !user ? (
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button data-testid="apply-signup" onClick={() => go("register")} className="flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gold text-ink text-xs uppercase tracking-[0.25em]">
              Create your account <ArrowRight size={14} />
            </button>
            <button data-testid="apply-signin" onClick={() => go("login")} className="px-8 py-4 rounded-full border border-white/15 text-xs uppercase tracking-[0.25em] text-text-muted hover:text-text-main">
              I already have one
            </button>
          </div>
        ) : !state ? <p className="text-center text-text-muted">Loading…</p>
          : state.application && state.application.status !== "rejected" ? <StatusCard a={state.application} isCreator={state.is_creator} />
          : state.is_creator ? <StatusCard a={{ status: "approved" }} isCreator />
          : (
            <>
              {state.application?.status === "rejected" && <StatusCard a={state.application} />}
              <ApplyForm user={user} onDone={(a) => setState({ ...state, application: a })} />
            </>
          )}
        <p className="text-center text-[11px] uppercase tracking-[0.3em] text-text-muted">18+ only · vellutosociety.com</p>
      </motion.div>
    </div>
  );
}
